// Knowledge base picks made with "@" in the composer, and the selection they
// add up to. See ChatInterface.js (mentionQuery, mentionDraft) and the
// backend's knowledge_scope_change.py, which applies the same rules.

// The composer's "@" mention (see mentionQuery in the component) always ADDS
// a file, never toggles one off -- unlike ProjectPanel.js's own
// toggleFileInScope (a checkbox, so it must support un-checking). Mentioning
// a file while today's implicit "no explicit selection" (scope null, or the
// explicit {mode:'all'} "@all" sets) is still in effect starts a fresh
// explicit selection containing just that one file, rather than "that file
// plus everything else" -- that's what makes "@file.pdf" actually narrow the
// knowledge base instead of being a no-op. See .claude/plan/plan_knowledge_scope.md.
export function addFileToKnowledgeScope(scope, fileId) {
  if (scope && scope.mode === 'selected') {
    if ((scope.file_ids || []).includes(fileId)) return scope;
    return { ...scope, file_ids: [...(scope.file_ids || []), fileId] };
  }
  return { mode: 'selected', file_ids: [fileId], folder_ids: [] };
}

// The other half of addFileToKnowledgeScope -- backspacing a whole "@file"
// pill out of the composer (see handleComposerKeyDown) undoes exactly what
// mentioning it did. A no-op when scope isn't an explicit selection (nothing
// to remove from -- the file is already in scope some other way), same as
// there being nothing for addFileToKnowledgeScope to add TO in that state.
// This is only ever reached via a composer backspace (ProjectPanel's own
// deliberate "clear all" checkbox flow calls toggleFileInScope directly, not
// this), so if removing the id leaves both arrays empty, the mention(s)
// that built this scope have all been undone -- normalize back to
// unrestricted (null) rather than leaving an explicit empty selection, which
// the backend (and ProjectPanel's own UI) treats as "no documents in scope".
export function removeFileFromKnowledgeScope(scope, fileId) {
  if (!scope || scope.mode !== 'selected') return scope;
  const file_ids = (scope.file_ids || []).filter((id) => id !== fileId);
  const folder_ids = scope.folder_ids || [];
  if (file_ids.length === 0 && folder_ids.length === 0) return null;
  return { ...scope, file_ids };
}

// Same three helpers as above, but for mentioning a whole Knowledge base
// folder (@folder-name) instead of a single file -- the backend already
// resolves a folder_id in scope to "every file currently filed under it"
// (a live reference, not a snapshot -- see ProjectFileFolder in
// conversation_models.py), so mentioning a folder is just adding its id to
// folder_ids rather than expanding it to individual file_ids here.
export function addFolderToKnowledgeScope(scope, folderId) {
  if (scope && scope.mode === 'selected') {
    if ((scope.folder_ids || []).includes(folderId)) return scope;
    return { ...scope, folder_ids: [...(scope.folder_ids || []), folderId] };
  }
  return { mode: 'selected', file_ids: [], folder_ids: [folderId] };
}

export function removeFolderFromKnowledgeScope(scope, folderId) {
  if (!scope || scope.mode !== 'selected') return scope;
  const folder_ids = (scope.folder_ids || []).filter((id) => id !== folderId);
  const file_ids = scope.file_ids || [];
  if (file_ids.length === 0 && folder_ids.length === 0) return null;
  return { ...scope, folder_ids };
}

// In a conversation that already exists, "@" picks aren't saved as they're
// made: they wait in the composer and go with the next question, which
// applies them when it runs (the backend's scope_after_picks, same rules as
// here). So a pick taken out again before sending changes nothing, and the
// "Knowledge base updated" note sits right above the question it was for.
export function scopeAfterPicks(scope, picks) {
  return (picks || []).reduce((current, pick) => {
    if (pick.op === 'all') return null;
    if (pick.op === 'add_file') return addFileToKnowledgeScope(current, pick.id);
    if (pick.op === 'add_folder') return addFolderToKnowledgeScope(current, pick.id);
    if (pick.op === 'remove_file') return removeFileFromKnowledgeScope(current, pick.id);
    if (pick.op === 'remove_folder') return removeFolderFromKnowledgeScope(current, pick.id);
    return current;
  }, scope || null);
}

// Backspacing a mention's pill undoes that mention: its own pick comes out
// of the unsent ones (the latest of them, if it was picked twice).
export function withoutPick(picks, op, id) {
  const at = (picks || []).map((p) => p.op === op && (op === 'all' || p.id === id)).lastIndexOf(true);
  return at === -1 ? picks : [...picks.slice(0, at), ...picks.slice(at + 1)];
}
